import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Timer, Clock, Target, TrendingDown } from "lucide-react";
import { parentExamHistory } from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
function fmtMinutes(seconds) {
  if (!seconds) return ",";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}
function scoreColor(pct) {
  if (pct >= 80) return "text-green-600";
  if (pct >= 60) return "text-yellow-600";
  return "text-orange-600";
}
function scoreBg(pct) {
  if (pct >= 80) return "bg-[#9DFFB0] dark:bg-[#68D391]";
  if (pct >= 60) return "bg-[#FFE066] dark:bg-[#F6E05E]";
  return "bg-[#FFB38E] dark:bg-[#F6AD55]";
}

export default function ParentExamHistory() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    parentExamHistory().then(setRows).catch(() => setRows([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950" data-testid="parent-exam-history-page">
      <header className="nb-border border-l-0 border-r-0 border-t-0 bg-white dark:bg-zinc-900 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate("/parent")}
            data-testid="exam-history-back"
            className="nb-border rounded-xl px-3 py-2 font-bold text-sm flex items-center gap-2 hover:nb-shadow"
          >
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
          <div className="font-display font-extrabold text-xl flex items-center gap-2">
            <Timer className="w-5 h-5" /> Mock exam history
          </div>
          <div className="w-24" />
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-6 sm:p-10">
        <div className="mb-6">
          <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400 max-w-xl">
            Read-only view of every mock exam your children have submitted. Your child starts and takes exams from their own account.
          </p>
        </div>

        {rows === null ? (
          <div className="font-bold text-zinc-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="nb-card p-10 text-center" data-testid="exam-history-empty">
            <Timer className="w-10 h-10 mx-auto mb-2" strokeWidth={2} />
            <div className="font-display text-xl font-extrabold mb-1">No exams yet</div>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 font-medium">
              Once your child completes a mock exam, their results will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="exam-history-list">
            {rows.map((r, i) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.03, 0.3) }}
                className="nb-card p-5"
                data-testid={`exam-row-${i}`}
              >
                <div className="flex items-start gap-4 flex-wrap">
                  <img
                    src={`https://api.dicebear.com/7.x/adventurer/svg?seed=${encodeURIComponent(r.child_avatar_seed || r.child_name || "x")}`}
                    alt=""
                    className="w-12 h-12 nb-border rounded-xl bg-[#D4C4FB] flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display text-lg font-extrabold">{r.child_name || ","}</div>
                      <div className="text-xs font-bold text-zinc-500">·</div>
                      <div className="font-bold text-sm">{r.subject}</div>
                    </div>
                    <div className="text-xs font-bold text-zinc-500 mt-0.5 flex items-center gap-3 flex-wrap">
                      <span>{fmtDate(r.submitted_at)}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtMinutes(r.seconds_taken)}</span>
                      <span className="flex items-center gap-1"><Target className="w-3 h-3" /> {r.correct}/{r.total}</span>
                    </div>
                  </div>
                  <div
                    className={`font-display text-3xl font-black ${scoreColor(r.score_pct ?? 0)} nb-border rounded-xl px-3 py-1.5 ${scoreBg(r.score_pct ?? 0)}`}
                    data-testid={`exam-score-${i}`}
                  >
                    {r.score_pct ?? 0}%
                  </div>
                </div>

                {r.per_topic?.length > 0 && (
                  <div className="mt-4 pt-4 nb-border border-l-0 border-r-0 border-b-0">
                    <div className="label-caps mb-2 flex items-center gap-1">
                      <TrendingDown className="w-3 h-3" /> Weakest topics
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {r.per_topic.slice(0, 4).map((t) => (
                        <div
                          key={t.topic_id}
                          className="nb-border rounded-full px-3 py-1 text-xs font-bold bg-white dark:bg-zinc-900 flex items-center gap-1.5"
                        >
                          <span className="truncate max-w-[180px]">{t.topic_name}</span>
                          <span className={`${scoreColor(t.pct)} font-black`}>{t.pct}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
